/**
 * The browser's part of a card payment and of saving a card, with Stripe.js. Pure functions
 * over a Stripe instance and a `post(url, data)` that resolves to { status, body } (never
 * rejects), so they are tested without a DOM (tests/js/card-payment.test.mjs).
 *
 * Paying: the server answers a launch or an add-budget with a PaymentIntent's client secret,
 * the saved card to pay with (or none, for the card typed into the page) and a confirm url.
 * The browser confirms with Stripe - 3D Secure happens here when the bank asks - and then
 * always tells the server, which settles the charge from what Stripe says (and emails the
 * client about a declined card). Nothing is ever credited on the browser's word.
 */

/**
 * @param {object} args
 * @param {object} args.stripe        Stripe.js instance
 * @param {object} args.response      { client_secret, payment_method, confirm_url }
 * @param {object|null} args.card     the card Element, when no saved card is used
 * @param {function} args.post        (url, data) => Promise<{ status, body }>
 * @returns {Promise<{ ok: boolean, pending: boolean, message: string }>}
 */
export async function payCharge({ stripe, response, card, post }) {
  const data = response.payment_method
    ? { payment_method: response.payment_method }
    : { payment_method: { card }, setup_future_usage: 'off_session' }

  let result
  try {
    result = await stripe.confirmCardPayment(response.client_secret, data)
  } catch (e) {
    result = { error: { message: 'We could not reach the payment provider. Please try again.' } }
  }

  const server = await post(response.confirm_url, {})

  if (result.error) {
    return { ok: false, pending: false, message: result.error.message || 'Your card was declined.' }
  }

  if (server.status === 200) {
    return { ok: true, pending: false, message: (server.body && server.body.message) || 'Your payment went through.' }
  }

  if (server.status === 202) {
    return { ok: true, pending: true, message: (server.body && server.body.message) || 'Your payment is being processed.' }
  }

  return { ok: false, pending: false, message: (server.body && server.body.message) || 'Your payment did not go through.' }
}

/**
 * Save a new card on the client's Stripe Customer through a SetupIntent.
 * @param {object} args
 * @param {object} args.stripe
 * @param {object} args.card          the card Element
 * @param {string} args.setupUrl      returns { client_secret }
 * @param {string} args.addUrl        takes { setup_intent_id }, returns { user_card_id }
 * @param {function} args.post
 * @returns {Promise<{ ok: boolean, message: string, userCardId?: number, cardType?: string, last4?: string }>}
 */
export async function saveCard({ stripe, card, setupUrl, addUrl, post }) {
  const setup = await post(setupUrl, {})
  if (setup.status !== 200 || !setup.body || !setup.body.client_secret) {
    return { ok: false, message: (setup.body && setup.body.message) || 'Something went wrong!' }
  }

  const result = await stripe.confirmCardSetup(setup.body.client_secret, { payment_method: { card } })
  if (result.error) {
    return { ok: false, message: result.error.message || 'This card could not be saved.' }
  }

  const added = await post(addUrl, { setup_intent_id: result.setupIntent.id })
  if (added.status !== 200) {
    return { ok: false, message: (added.body && added.body.message) || 'This card could not be saved.' }
  }

  return {
    ok: true,
    message: added.body.message || 'Success!',
    userCardId: added.body.user_card_id,
    cardType: added.body.card_type,
    last4: added.body.last4,
  }
}

/**
 * A `post` for the functions above, over jQuery (X-CSRF-TOKEN is added by bootstrap.js).
 * @param {object} $ jQuery
 */
export function jqueryPost($) {
  return (url, data) => new Promise((resolve) => {
    $.ajax({ url, type: 'POST', data })
      .done((body, _text, xhr) => resolve({ status: xhr.status, body }))
      .fail((xhr) => resolve({ status: xhr.status, body: xhr.responseJSON || {} }))
  })
}
